import { useId, useState, type FormEvent } from 'react';
import { Banner, Button, Dialog, Select, TextField } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { addDays, todayKey } from '../../calendar/dates';
import { assignExternal, useDeveloperOptions, type AssignExternalInput } from '../api';
import type { AfterSalesRecord } from '../types';
import styles from '../create/create.module.css';

const OTHER = 'other';

export interface AssignExternalDialogProps {
  record: AfterSalesRecord;
  onDone: () => Promise<void>;
  onClose: () => void;
}

/**
 * „An Handwerker geben …" (After-Sales 04 without frame; own texts): a developer of the organization or another
 * company, purpose, contact person, phone and the deadline (default in 7 days) → `POST /after-sales/:id/assign-external`.
 * The customer then reads „In Bearbeitung beim Bauträger. Wir melden uns mit einem Termin." in the portal.
 */
export function AssignExternalDialog({ record, onDone, onClose }: AssignExternalDialogProps) {
  const formId = useId();
  const developers = useDeveloperOptions(true);
  const [choice, setChoice] = useState('');
  const [company, setCompany] = useState('');
  const [purpose, setPurpose] = useState('Gewährleistung');
  const [contactPerson, setContactPerson] = useState('');
  const [phone, setPhone] = useState('');
  const [deadlineOn, setDeadlineOn] = useState(addDays(todayKey(), 7));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const items = developers.data?.items ?? [];
  const other = choice === OTHER || (items.length === 0 && !developers.isPending);

  function pick(value: string) {
    setChoice(value);
    const developer = items.find((item) => item.id === value);
    if (developer) {
      setContactPerson(developer.contactPerson ?? '');
      setPhone(developer.phone ?? '');
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (other && !company.trim()) next['company'] = 'Bitte eine Firma angeben.';
    if (!other && !choice) next['developerId'] = 'Bitte einen Bauträger wählen.';
    if (!deadlineOn) next['deadlineOn'] = 'Bitte ein Datum angeben.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    const body: AssignExternalInput = { deadlineOn };
    if (other) body.company = company.trim();
    else body.developerId = choice;
    if (purpose.trim()) body.purpose = purpose.trim();
    if (contactPerson.trim()) body.contactPerson = contactPerson.trim();
    if (phone.trim()) body.phone = phone.trim();
    setPending(true);
    setBanner(null);
    try {
      await assignExternal(record.id, body);
      await onDone();
      onClose();
    } catch (error) {
      if (error instanceof ApiError && Object.keys(error.fields).length > 0) setErrors(error.fields);
      else setBanner(errorMessage(error));
      setPending(false);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      width={600}
      eyebrow={record.numberLabel}
      title="An Handwerker geben"
      subtitle="Der Vorgang wechselt zu „Beim Handwerker“. Der Kunde sieht im Portal: „In Bearbeitung beim Bauträger. Wir melden uns mit einem Termin.“"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={pending}>
            Zuweisen
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        {items.length > 0 ? (
          <Select
            label="Bauträger"
            required
            placeholder="Bauträger wählen"
            value={choice}
            onChange={(event) => pick(event.target.value)}
            options={[
              ...items.map((item) => ({ value: item.id, label: item.name })),
              { value: OTHER, label: 'Andere Firma' },
            ]}
            error={errors['developerId']}
          />
        ) : null}
        {other ? (
          <TextField
            label="Firma"
            required
            value={company}
            maxLength={200}
            onChange={(event) => setCompany(event.target.value)}
            error={errors['company']}
          />
        ) : null}
        <TextField
          label="Zweck"
          optional
          value={purpose}
          maxLength={200}
          onChange={(event) => setPurpose(event.target.value)}
          error={errors['purpose']}
        />
        <div className={styles.row}>
          <TextField
            label="Ansprechpartner"
            optional
            value={contactPerson}
            maxLength={200}
            onChange={(event) => setContactPerson(event.target.value)}
            error={errors['contactPerson']}
          />
          <TextField
            label="Telefon"
            optional
            type="tel"
            value={phone}
            maxLength={60}
            onChange={(event) => setPhone(event.target.value)}
            error={errors['phone']}
          />
        </div>
        <TextField
          label="Frist"
          type="date"
          required
          value={deadlineOn}
          onChange={(event) => setDeadlineOn(event.target.value)}
          error={errors['deadlineOn']}
          hint="Standard: sieben Tage."
        />
      </form>
    </Dialog>
  );
}
